import React, { useCallback, useMemo } from "react";
import * as Scrivito from "scrivito";
import { ModalHeader } from "reactstrap";
import NiceModal from "@ebay/nice-modal-react";
import { useMutation } from "urql";

import { useTranslate, useUserStatuses } from "hooks";
import { useUserRole } from "hooks/useUserRole";
import { useUserGroup } from "hooks/useUserGroup";

import { UserDetailsForm } from "./UserDetailsForm";
import type { UserFormValues, StatusOption } from "./UserDetailsForm";
import { ModalWrapper } from "../../ModalWrapper";
import { useIsSameProj, useLanguage } from "stores/globalStore";
import type { SelectOption } from "interfaces/shared.types";

type UserProject = {
  id?: string;
  title?: string;
  constructionTitle?: string;
  projectNumber?: string;
  description?: string;
  status?: string;
};

type Props = {
  project?: UserProject;
  mutation: Parameters<typeof useMutation>[0];
  resetCallback?: () => void;
};

export const UserDetailsModal = NiceModal.create(
  Scrivito.connect(
    ({
      project = {},
      mutation,
      resetCallback = () => {},
    }: Props): React.ReactElement => {
      const translate = useTranslate();
      const isSameProj = useIsSameProj();
      const modal = NiceModal.useModal();
      const userStatuses = useUserStatuses();
      const lang = useLanguage();

      const [{ fetching, stale }, execute] = useMutation(mutation);

      const optionsList = useMemo(
        (): StatusOption[] | undefined =>
          userStatuses?.map((item) => ({
            value: item.status,
            label: translate(item.status) as string,
            color: item.color,
          })),
        [translate, userStatuses],
      );

      const userRole = useUserRole();
      const userGroup = useUserGroup();

      const rolesOptions = useMemo(
        (): SelectOption[] | undefined =>
          isSameProj
            ? (userRole?.map(({ value, label }) => ({ value, label })) ??
              undefined)
            : undefined,
        [userRole, isSameProj],
      );

      const group = useMemo(
        (): SelectOption[] | undefined =>
          isSameProj
            ? (userGroup?.map(({ value, label }) => ({ value, label })) ??
              undefined)
            : undefined,
        [userGroup, isSameProj],
      );

      const initialValues = useMemo(
        (): UserFormValues => ({
          status: optionsList?.find(
            (item) => item.value === (project.status ?? "draft"),
          ),
          country: { value: "User", label: "User" },
        }),
        [optionsList, project.status],
      );

      const handleSubmit = useCallback(
        (values: UserFormValues): void => {
          if (!fetching) {
            execute(
              project?.id
                ? {
                    id: project.id,
                    input: {
                      ...values,
                      status: values.status?.value ?? userStatuses?.[0]?.status,
                      group: values.group?.value,
                      units: values.units?.value,
                    },
                  }
                : {
                    ...values,
                    status: values.status?.value ?? userStatuses?.[0]?.status,
                    group: values.group?.value,
                    language: lang,
                  },
            ).then(({ data, error }) => {
              if (!error) {
                if (!project.id) {
                  modal.resolve({
                    ...(data as Record<string, Record<string, unknown>>)
                      .createProject,
                  });
                }
                modal.remove();
              }
            });
            resetCallback();
          }
        },
        [fetching, execute, project, userStatuses, lang, resetCallback, modal],
      );

      return (
        <ModalWrapper
          isOpen={modal.visible}
          centered={true}
          size="lg"
          toggle={modal.hide}
          isLoading={fetching || stale}
        >
          <ModalHeader
            toggle={modal.hide}
            tag="div"
            className="d-flex align-items-start"
          >
            <h3 className="text-uppercase mb-1">CREATE NEW USER</h3>
            <h4 className="mb-2_5">Please Define the User Properties</h4>
          </ModalHeader>
          <UserDetailsForm
            handleSubmit={handleSubmit}
            initialValues={initialValues}
            isUpdate={!!project.id}
            countriesOptions={rolesOptions}
            optionsList={optionsList}
            group={group}
          />
        </ModalWrapper>
      );
    },
  ),
);
